import { useEffect, useMemo, useRef, useState } from "react";
import MapView from "./MapView.jsx";
import CameraPanel from "./CameraPanel.jsx";
import PlaceSearch from "./PlaceSearch.jsx";
import RouteCameraSection from "./RouteCameras.jsx";
import { BestCard, SkeletonRows, StationRow, stationAddress, stationName } from "./StationList.jsx";
import { BrandBadge, Icon, PriceLegend, useMediaQuery } from "./ui.jsx";
import { loadCameras } from "./cameras.js";
import { getBrandOptions, stationMatchesBrand } from "./brands.js";
import { DEFAULT_ROAD_FACTOR, rankStations } from "./cost.js";
import { FUELS, geocode, loadStations } from "./data.js";
import { comma, formatKm, formatPrice, formatUpdatedAt } from "./format.js";
import { buildDecisionInsights } from "./insights.js";
import { addRecentPlace, normalizeRecentPlaces, RECENT_PLACES_KEY } from "./recentPlaces.js";
import { matchesQuery, searchStations, SORTS, sortLabel, sortStations } from "./stationCatalog.js";
import { routeForMap } from "./routeDisplay.js";
import { camerasAlongRoute } from "./routeCameras.js";
import { loadRoadMetrics, loadRoadRoute, selectNearbyCandidates, selectRouteCandidates } from "./routing.js";
import useSheet from "./useSheet.js";

const PREFERENCES_KEY = "petroprize:preferences:v2";
const THEME_KEY = "petroprize:theme";
const DEFAULTS = { fuel: "gasolina95", budget: 60, consumption: 6.5, radius: 10, tripMode: "roundTrip", remainingRange: "", sort: "best" };
const LIMITS = { budget: [10, 300], consumption: [1, 30], radius: [1, 50], remainingRange: [10, 1500] };
const TRIP_MODES = { roundTrip: "Ida y vuelta", oneWay: "Solo ida", enRoute: "En ruta" };

function storageGet(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}
function storageSet(key, value) {
  try { localStorage.setItem(key, value); } catch { /* Almacenamiento no disponible: la sesión sigue funcionando. */ }
}

function validNumber(value, [min, max]) {
  if (value === "" || value == null) return null;
  const number = Number(String(value).replace(",", "."));
  return Number.isFinite(number) && number >= min && number <= max ? number : null;
}

function readPreferences() {
  try {
    const saved = JSON.parse(storageGet(PREFERENCES_KEY) || "{}");
    const preferences = { ...DEFAULTS, ...saved };
    if (!Object.hasOwn(TRIP_MODES, preferences.tripMode)) preferences.tripMode = DEFAULTS.tripMode;
    if (!Object.hasOwn(FUELS, preferences.fuel)) preferences.fuel = DEFAULTS.fuel;
    if (!SORTS.includes(preferences.sort)) preferences.sort = DEFAULTS.sort;
    for (const key of ["budget", "consumption", "radius"]) if (validNumber(preferences[key], LIMITS[key]) == null) preferences[key] = DEFAULTS[key];
    if (preferences.remainingRange !== "" && validNumber(preferences.remainingRange, LIMITS.remainingRange) == null) preferences.remainingRange = "";
    return preferences;
  } catch {
    return DEFAULTS;
  }
}

function readTheme() {
  const saved = storageGet(THEME_KEY);
  return saved === "light" || saved === "dark" ? saved : "dark";
}

function readRecentPlaces() {
  try { return normalizeRecentPlaces(JSON.parse(storageGet(RECENT_PLACES_KEY) || "[]")); } catch { return []; }
}

// Mantiene el último valor válido mientras el usuario escribe uno incorrecto.
function useAppliedNumber(draft, limits, optional = false) {
  const last = useRef(validNumber(draft, limits));
  const parsed = validNumber(draft, limits);
  if (parsed != null) last.current = parsed;
  if (optional && draft === "") last.current = null;
  const invalid = draft !== "" ? parsed == null : !optional;
  return [last.current, invalid];
}

function NumberField({ id, label, value, onChange, unit, limits, invalid, placeholder, step }) {
  return (
    <div className={`number-field${invalid ? " is-invalid" : ""}`}>
      <label htmlFor={id}>{label}</label>
      <span className="number-input">
        <input id={id} type="number" inputMode="decimal" min={limits[0]} max={limits[1]} step={step} value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} aria-invalid={invalid} aria-describedby={invalid ? `${id}-error` : undefined} />
        <span>{unit}</span>
      </span>
      {invalid && <small id={`${id}-error`}>Entre {comma(limits[0], 0)} y {comma(limits[1], 0)}</small>}
    </div>
  );
}

export default function App() {
  const initial = useMemo(readPreferences, []);
  const isDesktop = useMediaQuery("(min-width: 980px)");
  const workspaceRef = useRef(null);
  const panelScrollRef = useRef(null);
  const sheet = useSheet(workspaceRef, "peek");
  const [mode, setMode] = useState("fuel");
  const [fuelMapLayer, setFuelMapLayer] = useState("stations");
  const [cameraContextRequested, setCameraContextRequested] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [cameraReloadToken, setCameraReloadToken] = useState(0);
  const [selectedCameraId, setSelectedCameraId] = useState(null);
  const [mapError, setMapError] = useState("");
  const [theme, setTheme] = useState(readTheme);
  const [allStations, setAllStations] = useState([]);
  const [meta, setMeta] = useState({ updatedAt: "", isFallback: false });
  const [origin, setOrigin] = useState(null);
  const [originLabel, setOriginLabel] = useState("");
  const [query, setQuery] = useState("");
  const [fuel, setFuel] = useState(initial.fuel);
  const [budgetDraft, setBudgetDraft] = useState(String(initial.budget));
  const [consumptionDraft, setConsumptionDraft] = useState(String(initial.consumption));
  const [radiusDraft, setRadiusDraft] = useState(String(initial.radius));
  const [remainingRangeDraft, setRemainingRangeDraft] = useState(String(initial.remainingRange ?? ""));
  const [tripMode, setTripMode] = useState(initial.tripMode);
  const [sort, setSort] = useState(initial.sort);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [destination, setDestination] = useState(null);
  const [destinationLabel, setDestinationLabel] = useState("");
  const [destinationQuery, setDestinationQuery] = useState("");
  const [brandFilter, setBrandFilter] = useState("all");
  const [stationQuery, setStationQuery] = useState("");
  const [requestedStationId, setRequestedStationId] = useState(null);
  const [requestedRoad, setRequestedRoad] = useState(null);
  const [requestedRoadStatus, setRequestedRoadStatus] = useState("idle");
  const [selectedId, setSelectedId] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [dataError, setDataError] = useState(false);
  const [roadStatus, setRoadStatus] = useState("estimated");
  const [baseRoadRoute, setBaseRoadRoute] = useState(null);
  const [inspectedRoadRoute, setInspectedRoadRoute] = useState(null);
  const [roadMetrics, setRoadMetrics] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchingDestination, setSearchingDestination] = useState(false);
  const [notice, setNotice] = useState("");
  const [recentPlaces, setRecentPlaces] = useState(readRecentPlaces);
  const [pendingAreaCenter, setPendingAreaCenter] = useState(null);
  const searchController = useRef(null);
  const destinationController = useRef(null);
  const revealSelection = useRef(false);

  const [budget, budgetInvalid] = useAppliedNumber(budgetDraft, LIMITS.budget);
  const [consumption, consumptionInvalid] = useAppliedNumber(consumptionDraft, LIMITS.consumption);
  const [radius, radiusInvalid] = useAppliedNumber(radiusDraft, LIMITS.radius);
  const [remainingRange, remainingRangeInvalid] = useAppliedNumber(remainingRangeDraft, LIMITS.remainingRange, true);

  useEffect(() => {
    const controller = new AbortController();
    loadStations(controller.signal)
      .then((payload) => {
        setAllStations(payload.stations);
        setMeta({ updatedAt: payload.updatedAt, isFallback: payload.isFallback });
        setDataError(false);
      })
      .catch((error) => { if (error.name !== "AbortError") setDataError(true); })
      .finally(() => setLoadingData(false));
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!(mode === "cameras" || cameraContextRequested || (mode === "fuel" && tripMode === "enRoute" && origin && destination)) || cameras.length) return undefined;
    const controller = new AbortController();
    setCameraLoading(true);
    setCameraError("");
    loadCameras(controller.signal)
      .then((payload) => setCameras(payload.cameras))
      .catch((error) => { if (error.name !== "AbortError") setCameraError(error.message); })
      .finally(() => { if (!controller.signal.aborted) setCameraLoading(false); });
    return () => controller.abort();
  }, [mode, cameras.length, cameraReloadToken, cameraContextRequested, tripMode, origin, destination]);

  useEffect(() => {
    storageSet(PREFERENCES_KEY, JSON.stringify({ fuel, budget, consumption, radius, tripMode, remainingRange: remainingRange ?? "", sort }));
  }, [fuel, budget, consumption, radius, remainingRange, tripMode, sort]);

  useEffect(() => { storageSet(RECENT_PLACES_KEY, JSON.stringify(recentPlaces)); }, [recentPlaces]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    storageSet(THEME_KEY, theme);
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", theme === "dark" ? "#141c21" : "#ffffff");
  }, [theme]);

  const rankOptions = { origin, destination, tripMode, radiusKm: radius, budgetEuro: budget, consumptionLPer100Km: consumption, roadFactor: DEFAULT_ROAD_FACTOR, remainingRangeKm: remainingRange };

  const estimatedRanked = useMemo(() => (origin ? rankStations(allStations, fuel, rankOptions) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allStations, budget, consumption, destination, fuel, origin, radius, remainingRange, tripMode]);

  useEffect(() => {
    const controller = new AbortController();
    if (tripMode !== "enRoute" || !origin || !destination) {
      setBaseRoadRoute(null);
      return () => controller.abort();
    }
    setRoadStatus("loading");
    setBaseRoadRoute(null);
    loadRoadRoute(origin, destination, controller.signal)
      .then(setBaseRoadRoute)
      .catch((error) => { if (error.name !== "AbortError") setRoadStatus("estimated"); });
    return () => controller.abort();
  }, [destination, origin, tripMode]);

  const roadCandidates = useMemo(() => {
    if (!origin) return [];
    if (tripMode === "enRoute") {
      if (!destination || !baseRoadRoute?.geometry) return [];
      return selectRouteCandidates(allStations, fuel, baseRoadRoute.geometry, radius, origin, remainingRange);
    }
    return selectNearbyCandidates(allStations, fuel, origin, radius);
  }, [allStations, baseRoadRoute, destination, fuel, origin, radius, remainingRange, tripMode]);
  const roadCandidateKey = useMemo(() => roadCandidates.map((station) => station.id).join(","), [roadCandidates]);

  useEffect(() => {
    const controller = new AbortController();
    if (!origin || !roadCandidates.length || (tripMode === "enRoute" && !destination)) {
      setRoadMetrics(null);
      setRoadStatus("estimated");
      return () => controller.abort();
    }
    setRoadStatus("loading");
    setRoadMetrics(null);
    loadRoadMetrics(origin, tripMode === "enRoute" ? destination : null, roadCandidates, controller.signal)
      .then((payload) => {
        setRoadMetrics(new Map(payload.items.map((item) => [item.id, item])));
        setRoadStatus("routed");
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          setRoadMetrics(null);
          setRoadStatus("estimated");
        }
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination, origin, roadCandidateKey, tripMode]);

  const routeKey = origin ? `${origin.lat},${origin.lng}:${destination?.lat ?? "-"},${destination?.lng ?? "-"}:${tripMode}` : "";

  const unfilteredRanked = useMemo(() => {
    if (roadStatus !== "routed" || !roadMetrics) return estimatedRanked;
    const combinedMetrics = new Map(roadMetrics);
    if (requestedRoad?.id && requestedRoad.key === routeKey) combinedMetrics.set(requestedRoad.id, requestedRoad.metric);
    return rankStations(allStations, fuel, { ...rankOptions, roadMetrics: combinedMetrics, requireRoadMetrics: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStations, budget, consumption, destination, estimatedRanked, fuel, origin, radius, remainingRange, requestedRoad, roadMetrics, roadStatus, tripMode]);

  const brandOptions = useMemo(() => getBrandOptions(estimatedRanked), [estimatedRanked]);
  const activeBrand = brandOptions.find((brand) => brand.id === brandFilter) || null;
  const ranked = useMemo(() => unfilteredRanked.filter((station) => stationMatchesBrand(station, brandFilter)), [brandFilter, unfilteredRanked]);
  const listStations = useMemo(() => sortStations(ranked.filter((station) => matchesQuery(station, stationQuery)), sort, tripMode), [ranked, sort, stationQuery, tripMode]);
  const rankedIds = useMemo(() => new Set(ranked.map((station) => station.id)), [ranked]);
  const filtering = brandFilter !== "all" || stationQuery.trim() !== "";
  const extraMatches = useMemo(
    () => (filtering ? searchStations(estimatedRanked, brandFilter, stationQuery).filter((station) => !rankedIds.has(station.id) && station.id !== requestedStationId) : []),
    [brandFilter, estimatedRanked, filtering, rankedIds, requestedStationId, stationQuery],
  );

  useEffect(() => {
    if (brandFilter !== "all" && !brandOptions.some((brand) => brand.id === brandFilter)) setBrandFilter("all");
  }, [brandFilter, brandOptions]);

  const best = ranked[0] || null;
  const selected = ranked.find((station) => station.id === selectedId) || null;
  const requestedStation = estimatedRanked.find((station) => station.id === requestedStationId) || null;
  const requestedCalculated = useMemo(() => {
    if (!requestedStation || !requestedRoad?.metric || !origin) return null;
    if (requestedRoad.id !== requestedStation.id || requestedRoad.key !== routeKey) return null;
    return rankStations([requestedStation], fuel, { ...rankOptions, radiusKm: Infinity, remainingRangeKm: null, roadMetrics: new Map([[requestedStation.id, requestedRoad.metric]]), requireRoadMetrics: true })[0] || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budget, consumption, destination, fuel, origin, requestedRoad, requestedStation, tripMode]);
  const requestedWithinRoadRadius = requestedCalculated && (tripMode === "enRoute"
    ? requestedCalculated.estimate.travelKm <= radius
    : requestedCalculated.estimate.oneWayRoadKm <= radius);
  const routeTarget = requestedStation || selected || best;
  const routeTone = routeTarget && routeTarget.id !== best?.id ? "selected" : "best";
  const selectedCamera = cameras.find((camera) => camera.id === selectedCameraId) || null;
  const routeCameras = useMemo(() => (tripMode === "enRoute" ? camerasAlongRoute(cameras, baseRoadRoute?.geometry) : []), [cameras, baseRoadRoute, tripMode]);
  const displayedRoute = routeForMap({ origin, destination, station: routeTarget, tripMode, baseRoadRoute, inspectedRoadRoute });
  const decisionInsights = useMemo(() => buildDecisionInsights(ranked), [ranked]);

  useEffect(() => {
    const controller = new AbortController();
    if (!origin || !routeTarget || (tripMode === "enRoute" && !destination)) {
      setInspectedRoadRoute(null);
      return () => controller.abort();
    }
    setInspectedRoadRoute(null);
    const routeDestination = tripMode === "enRoute" ? destination : routeTarget;
    const via = tripMode === "enRoute" ? routeTarget : null;
    loadRoadRoute(origin, routeDestination, controller.signal, via)
      .then(setInspectedRoadRoute)
      .catch(() => setInspectedRoadRoute(null));
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination, origin, routeTarget?.id, tripMode]);

  useEffect(() => {
    const controller = new AbortController();
    if (!requestedStation || !origin || (tripMode === "enRoute" && !destination)) return () => controller.abort();
    if (roadMetrics?.has(requestedStation.id)) {
      setRequestedRoad({ id: requestedStation.id, metric: roadMetrics.get(requestedStation.id), key: routeKey });
      setRequestedRoadStatus("routed");
      return () => controller.abort();
    }
    setRequestedRoad(null);
    setRequestedRoadStatus("loading");
    loadRoadMetrics(origin, tripMode === "enRoute" ? destination : null, [requestedStation], controller.signal)
      .then((payload) => {
        const metric = payload.items.find((item) => item.id === requestedStation.id);
        if (!metric) throw new Error("Sin distancia por carretera");
        setRequestedRoad({ id: requestedStation.id, metric, key: routeKey });
        setRequestedRoadStatus("routed");
      })
      .catch((error) => { if (error.name !== "AbortError") setRequestedRoadStatus("error"); });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination, origin, requestedStation?.id, roadMetrics, tripMode]);

  // Al elegir en el mapa, la fila correspondiente se muestra en la lista. Se desplaza
  // solo el panel: scrollIntoView movería también contenedores con overflow oculto.
  useEffect(() => {
    if (!revealSelection.current || !selectedId) return;
    revealSelection.current = false;
    window.requestAnimationFrame(() => {
      const scroller = panelScrollRef.current;
      const row = document.getElementById(`station-${selectedId}`);
      if (!scroller || !row) return;
      const top = row.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
      const bottom = top + row.offsetHeight;
      if (top < scroller.scrollTop || bottom > scroller.scrollTop + scroller.clientHeight) scroller.scrollTop = Math.max(0, top - 12);
    });
  }, [selectedId]);

  // La página nunca debe desplazarse: todo el scroll ocurre dentro del panel.
  useEffect(() => {
    const reset = () => { if (window.scrollY || window.scrollX) window.scrollTo(0, 0); };
    window.addEventListener("scroll", reset, { passive: true });
    return () => window.removeEventListener("scroll", reset);
  }, []);

  function openSheet(target = "half") {
    if (!isDesktop && (sheet.snap === "peek" || target === "full")) sheet.setSnap(target);
  }

  function applyOrigin(place, { remember = false } = {}) {
    setOrigin({ lat: Number(place.lat), lng: Number(place.lng) });
    setOriginLabel(place.label);
    setQuery(place.query || "");
    setSelectedId(null);
    setRequestedStationId(null);
    setBrandFilter("all");
    setStationQuery("");
    setPendingAreaCenter(null);
    openSheet("half");
    if (remember) setRecentPlaces((current) => addRecentPlace(current, place));
  }

  async function handleSearch(event) {
    event.preventDefault();
    const cleanQuery = query.trim();
    if (!cleanQuery) {
      setNotice("Escribe una calle, un barrio o una localidad.");
      return;
    }
    document.activeElement?.blur?.();
    searchController.current?.abort();
    const controller = new AbortController();
    searchController.current = controller;
    setSearching(true);
    setNotice("");
    try {
      const [result] = await geocode(cleanQuery, controller.signal);
      applyOrigin({ lat: Number(result.lat), lng: Number(result.lng), label: result.label || cleanQuery, query: cleanQuery }, { remember: true });
    } catch (error) {
      if (error.name !== "AbortError") setNotice("No encontramos ese lugar en España. Prueba con la localidad o añade la provincia.");
    } finally {
      setSearching(false);
    }
  }

  function useMyLocation() {
    if (!navigator.geolocation) {
      setNotice("Este navegador no permite usar tu ubicación. Escribe una dirección.");
      return;
    }
    setSearching(true);
    setNotice("");
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        applyOrigin({ lat: coords.latitude, lng: coords.longitude, label: "Tu ubicación", query: "" });
        setSearching(false);
      },
      () => {
        setNotice("No se pudo obtener tu ubicación. Revisa el permiso del navegador o escribe una dirección.");
        setSearching(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 120000 },
    );
  }

  async function handleDestinationSearch(event) {
    event.preventDefault();
    const cleanQuery = destinationQuery.trim();
    if (!cleanQuery) {
      setNotice("Escribe el destino de tu viaje.");
      return;
    }
    document.activeElement?.blur?.();
    destinationController.current?.abort();
    const controller = new AbortController();
    destinationController.current = controller;
    setSearchingDestination(true);
    setNotice("");
    try {
      const [result] = await geocode(cleanQuery, controller.signal);
      setDestination({ lat: Number(result.lat), lng: Number(result.lng) });
      setDestinationLabel(result.label || cleanQuery);
      setSelectedId(null);
      setRequestedStationId(null);
      setBrandFilter("all");
      setStationQuery("");
    } catch (error) {
      if (error.name !== "AbortError") setNotice("No encontramos ese destino en España. Prueba con la localidad o añade la provincia.");
    } finally {
      setSearchingDestination(false);
    }
  }

  function changeTripMode(value) {
    setTripMode(value);
    setFuelMapLayer("stations");
    setSelectedId(null);
    setRequestedStationId(null);
    setBrandFilter("all");
    setStationQuery("");
  }

  function changeMode(value) {
    setMode(value);
    setMapError("");
    if (value === "fuel") setFuelMapLayer("stations");
  }

  function selectFromList(id) {
    setRequestedStationId(null);
    setSelectedId((current) => (current === id ? null : id));
  }

  function selectFromMap(id) {
    if (rankedIds.has(id)) {
      revealSelection.current = true;
      setRequestedStationId(null);
      setSelectedId(id);
    } else {
      setRequestedStationId(id);
    }
    openSheet("half");
  }

  const updatedLabel = formatUpdatedAt(meta.updatedAt);
  const dataStatus = loadingData ? { tone: "loading", text: "Cargando precios…" }
    : dataError ? { tone: "error", text: "Precios no disponibles" }
    : meta.isFallback ? { tone: "warning", text: "Copia local de precios" }
    : { tone: "ok", text: updatedLabel ? `Precios oficiales · ${updatedLabel}` : "Precios oficiales" };

  const sheetSummary = mode === "cameras"
    ? (selectedCamera?.name || (cameraLoading ? "Cargando cámaras…" : cameraError ? "Cámaras no disponibles" : `${cameras.length.toLocaleString("es-ES")} cámaras de la DGT`))
    : loadingData ? "Cargando precios…"
    : dataError ? "No se pudieron cargar los precios"
    : !origin ? "Busca desde dónde sales"
    : tripMode === "enRoute" && !destination ? "Indica el destino del viaje"
    : best ? `${stationName((requestedCalculated || selected || best))} · ${formatPrice((requestedCalculated || selected || best).estimate.effectivePrice)} con desplazamiento`
    : "Sin resultados con estos ajustes";

  const tripSummary = tripMode === "enRoute"
    ? `En ruta · desvío máx. ${comma(radius, 0)} km · ${comma(budget, 0)} € · ${comma(consumption, 1)} L/100 km`
    : `${TRIP_MODES[tripMode]} · ${comma(radius, 0)} km · ${comma(budget, 0)} € · ${comma(consumption, 1)} L/100 km`;
  const routingText = roadStatus === "loading" ? "Calculando distancias…" : roadStatus === "routed" ? "Distancias por carretera" : "Distancias estimadas";
  const bestLabel = activeBrand ? `Mejor ${activeBrand.label} con desplazamiento` : "Mejor opción con desplazamiento";
  const priceMode = sort === "cheapest" ? "pump" : "trip";
  const insets = isDesktop ? { bottom: 0, top: 0 } : { bottom: Math.min(sheet.visible, sheet.halfHeight), top: mode === "fuel" ? 64 : 0 };
  const fitTarget = useMemo(() => {
    if (!origin || tripMode === "enRoute" || !ranked.length) return null;
    return { key: `${routeKey}|${fuel}|${radius}|${brandFilter}`, points: [origin, ...ranked.slice(0, 5)] };
  }, [brandFilter, fuel, origin, radius, ranked, routeKey, tripMode]);

  const placeSearch = (
    <PlaceSearch
      id="location"
      label="Desde"
      value={query}
      onChange={setQuery}
      onSubmit={handleSearch}
      busy={searching}
      disabled={loadingData}
      placeholder="Dirección o localidad"
      recentPlaces={recentPlaces}
      onPickRecent={(place) => applyOrigin(place, { remember: true })}
      onLocate={useMyLocation}
      hint={originLabel && originLabel !== query ? originLabel : ""}
    />
  );

  const fuelHead = (
    <div className="panel-head">
      {isDesktop && placeSearch}
      <div className="segmented fuels" role="group" aria-label="Combustible">
        {Object.entries(FUELS).map(([value, label]) => (
          <button key={value} type="button" aria-pressed={fuel === value} onClick={() => setFuel(value)}>{label}</button>
        ))}
      </div>
      <div className="trip-summary">
        <span>{tripSummary}</span>
        <button type="button" className="link-button" aria-expanded={settingsOpen} aria-controls="settings" onClick={() => setSettingsOpen((open) => !open)}>
          {settingsOpen ? "Listo" : "Ajustar"}
        </button>
      </div>
      {settingsOpen && (
        <div className="settings" id="settings">
          <div className="segmented" role="group" aria-label="Tipo de desplazamiento">
            {Object.entries(TRIP_MODES).map(([value, label]) => (
              <button key={value} type="button" aria-pressed={tripMode === value} onClick={() => changeTripMode(value)}>{label}</button>
            ))}
          </div>
          <div className="settings-grid">
            <NumberField id="budget" label="Repostaje" value={budgetDraft} onChange={setBudgetDraft} unit="€" limits={LIMITS.budget} invalid={budgetInvalid} step="5" />
            <NumberField id="consumption" label="Consumo" value={consumptionDraft} onChange={setConsumptionDraft} unit="L/100 km" limits={LIMITS.consumption} invalid={consumptionInvalid} step="0.1" />
            <NumberField id="radius" label={tripMode === "enRoute" ? "Desvío máximo" : "Radio máximo"} value={radiusDraft} onChange={setRadiusDraft} unit="km" limits={LIMITS.radius} invalid={radiusInvalid} step="1" />
            {tripMode === "enRoute" && <NumberField id="range" label="Autonomía" value={remainingRangeDraft} onChange={setRemainingRangeDraft} unit="km" limits={LIMITS.remainingRange} invalid={remainingRangeInvalid} placeholder="Opcional" step="10" />}
          </div>
          <p className="fine">
            {tripMode === "enRoute"
              ? `Se cuenta solo el desvío respecto al trayecto directo.${remainingRange != null ? ` Solo se muestran paradas a menos de ${comma(remainingRange, 0)} km.` : ""}`
              : `Mientras no hay distancia por carretera, se estima con la línea recta más un 25 %${tripMode === "roundTrip" ? ", contando ida y vuelta" : ", contando solo la ida"}.`}
          </p>
        </div>
      )}
      {tripMode === "enRoute" && (
        <form className="destination" onSubmit={handleDestinationSearch}>
          <div className="place-field">
            <Icon name="flag" />
            <label className="place-label" htmlFor="destination">Hasta</label>
            <input id="destination" type="search" value={destinationQuery} onChange={(event) => setDestinationQuery(event.target.value)} placeholder="Destino del viaje" autoComplete="off" enterKeyHint="search" />
            {searchingDestination && <span className="spinner" aria-label="Buscando" />}
            <button className="field-button is-submit" type="submit" disabled={searchingDestination} aria-label="Buscar destino"><Icon name="search" /></button>
          </div>
          {destinationLabel && <p className="place-hint">{destinationLabel}</p>}
        </form>
      )}
    </div>
  );

  function fuelBody() {
    if (loadingData) return <SkeletonRows />;
    if (dataError) {
      return (
        <div className="empty">
          <h2>No hemos podido cargar los precios</h2>
          <p>El servicio del Ministerio no responde ahora mismo. Vuelve a intentarlo en unos segundos.</p>
          <button className="button is-primary" type="button" onClick={() => window.location.reload()}>Reintentar</button>
        </div>
      );
    }
    if (!origin) {
      return (
        <div className="empty">
          <span className="empty-icon"><Icon name="drop" /></span>
          <h2>Encuentra la gasolinera que de verdad compensa</h2>
          <p>Dinos desde dónde sales. Al precio del surtidor le sumamos lo que gastas en llegar.</p>
          <button className="button is-quiet" type="button" onClick={useMyLocation}><Icon name="locate" /> Usar mi ubicación</button>
        </div>
      );
    }
    if (tripMode === "enRoute" && !destination) {
      return (
        <div className="empty">
          <span className="empty-icon"><Icon name="flag" /></span>
          <h2>¿Hacia dónde vas?</h2>
          <p>Escribe el destino en el campo «Hasta» y te mostramos las gasolineras que menos desvío añaden.</p>
        </div>
      );
    }
    return (
      <>
        {best && <BestCard station={best} label={bestLabel} insights={decisionInsights} tripMode={tripMode} />}
        {!best && !filtering && (
          <div className="empty">
            <h2>No hay gasolineras con estos ajustes</h2>
            <p>{tripMode === "enRoute" && remainingRange != null
              ? "Ninguna está al alcance de tu autonomía. Revisa los kilómetros o amplía el desvío."
              : "Amplía el radio o cambia de combustible."}</p>
          </div>
        )}

        {tripMode === "enRoute" && destination && (
          <RouteCameraSection
            cameras={routeCameras}
            geometry={baseRoadRoute?.geometry}
            loading={cameraLoading}
            error={cameraError}
            mapLayer={fuelMapLayer}
            selectedCameraId={selectedCameraId}
            onMapLayerChange={setFuelMapLayer}
            onRetry={() => setCameraReloadToken((value) => value + 1)}
            onCameraSelect={(id) => { setSelectedCameraId(id); setFuelMapLayer("cameras"); }}
          />
        )}

        {(best || filtering) && (
          <section className="list" aria-label="Comparativa de gasolineras">
            <div className="list-head">
              <h3>{listStations.length} {listStations.length === 1 ? "gasolinera" : "gasolineras"} {tripMode === "enRoute" ? `con hasta ${comma(radius, 0)} km de desvío` : `en ${comma(radius, 0)} km`}</h3>
              <label className="sort" htmlFor="sort">
                <span className="visually-hidden">Ordenar por</span>
                <select id="sort" value={sort} onChange={(event) => setSort(event.target.value)}>
                  {SORTS.map((value) => <option key={value} value={value}>{sortLabel(value, tripMode)}</option>)}
                </select>
                <Icon name="chevron" />
              </label>
            </div>
            <div className="list-meta">
              <PriceLegend />
              <p className={`routing is-${roadStatus}`}><span aria-hidden="true" />{routingText}</p>
            </div>

            {brandOptions.length > 0 && (
              <div className="chips" role="group" aria-label="Filtrar por marca">
                <button type="button" className="chip" aria-pressed={brandFilter === "all"} onClick={() => setBrandFilter("all")}>Todas <b>{estimatedRanked.length}</b></button>
                {brandOptions.map((brand) => (
                  <button key={brand.id} type="button" className="chip" aria-pressed={brandFilter === brand.id} onClick={() => { setBrandFilter(brand.id); setSelectedId(null); }}>
                    {brand.id !== "other" && <BrandBadge brand={brand} />}{brand.label} <b>{brand.count}</b>
                  </button>
                ))}
              </div>
            )}
            <label className="filter-field" htmlFor="station-query">
              <Icon name="search" />
              <input id="station-query" type="search" value={stationQuery} onChange={(event) => setStationQuery(event.target.value)} placeholder="Buscar por nombre o dirección" />
            </label>

            {listStations.length > 0 ? (
              <ul className="station-list">
                {listStations.slice(0, 30).map((station, index) => (
                  <StationRow
                    key={station.id}
                    station={station}
                    position={index + 1}
                    best={best}
                    isBest={station.id === best?.id}
                    selected={station.id === selectedId}
                    onSelect={() => selectFromList(station.id)}
                    tripMode={tripMode}
                    primary={priceMode}
                    origin={origin}
                    cameraProps={{
                      cameras,
                      cameraLoading,
                      cameraError,
                      onRequestCameras: () => { setCameraContextRequested(true); if (cameraError) setCameraReloadToken((value) => value + 1); },
                    }}
                  />
                ))}
              </ul>
            ) : (
              <p className="fine">Ninguna gasolinera de la comparativa coincide con el filtro.</p>
            )}

            {requestedStation && (
              <div className="requested" role="status">
                <BrandBadge station={requestedStation} />
                <div>
                  <b>{stationName(requestedStation)}</b>
                  <span>{stationAddress(requestedStation)}</span>
                  <span>
                    {requestedRoadStatus === "loading" ? "Calculando la distancia por carretera…"
                      : requestedRoadStatus === "error" ? "No se pudo calcular la carretera; se muestra la estimación."
                      : requestedCalculated ? `${formatKm(requestedCalculated.estimate.oneWayRoadKm)} por carretera · ${formatPrice(requestedCalculated.estimate.effectivePrice)} con desplazamiento`
                      : "Distancia estimada"}
                  </span>
                  {requestedCalculated && <span className={`tag ${requestedWithinRoadRadius ? "is-best" : "is-out"}`}>{requestedWithinRoadRadius ? "Dentro del radio por carretera" : "Fuera del radio por carretera"}</span>}
                </div>
                <button type="button" className="icon-button" aria-label="Cerrar" onClick={() => setRequestedStationId(null)}><Icon name="close" /></button>
              </div>
            )}

            {extraMatches.length > 0 && (
              <div className="extra-matches">
                <h4>Otras coincidencias en el radio</h4>
                <p className="fine">Aún no tienen distancia por carretera. Elige una para calcularla.</p>
                <ul>
                  {extraMatches.slice(0, 12).map((station) => (
                    <li key={station.id}>
                      <button type="button" aria-pressed={requestedStationId === station.id} onClick={() => setRequestedStationId(station.id)}>
                        <BrandBadge station={station} />
                        <span className="row-text"><span className="row-name">{stationName(station)}</span><span className="row-sub">{stationAddress(station)}</span></span>
                        <span className="caption">{formatPrice(station.pumpPrice)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}
      </>
    );
  }

  return (
    <div className={`app is-${mode}`}>
      <header className="topbar">
        <a className="logo" href="/" aria-label="PetroPrize, inicio">
          <span className="logo-mark"><Icon name="drop" /></span>
          <span className="logo-text">PetroPrize</span>
        </a>
        <nav className="segmented modes" aria-label="Modo">
          <button type="button" aria-pressed={mode === "fuel"} onClick={() => changeMode("fuel")}>Gasolineras</button>
          <button type="button" aria-pressed={mode === "cameras"} onClick={() => changeMode("cameras")}>Cámaras DGT</button>
        </nav>
        <div className="topbar-end">
          <span className={`data-status is-${dataStatus.tone}`}><span aria-hidden="true" />{dataStatus.text}</span>
          <button className="icon-button" type="button" onClick={() => setTheme((current) => (current === "dark" ? "light" : "dark"))} aria-label={theme === "dark" ? "Cambiar a tema claro" : "Cambiar a tema oscuro"}>
            <Icon name={theme === "dark" ? "sun" : "moon"} />
          </button>
        </div>
      </header>

      <main className="workspace" ref={workspaceRef}>
        <section className="map-area" aria-label={mode === "fuel" ? "Mapa de gasolineras" : "Mapa de cámaras"}>
          <MapView
            theme={theme}
            mode={mode}
            fuelMapLayer={fuelMapLayer}
            origin={origin}
            destination={destination}
            tripMode={tripMode}
            stations={listStations}
            requestedStation={requestedCalculated || requestedStation}
            cameras={cameras}
            contextCameras={routeCameras}
            selectedCamera={selectedCamera}
            selectedId={selected?.id}
            bestId={best?.id}
            routeGeometry={mode === "fuel" ? displayedRoute.geometry : null}
            routeEstimated={displayedRoute.estimated}
            routeTone={routeTone}
            insets={insets}
            fitTarget={fitTarget}
            priceMode={priceMode}
            onSelect={selectFromMap}
            onCameraSelect={(id) => { setSelectedCameraId(id); openSheet("half"); }}
            onAreaChange={setPendingAreaCenter}
            onMapError={setMapError}
          />
          {!isDesktop && mode === "fuel" && <div className="map-search">{placeSearch}</div>}
          {isDesktop && mode === "fuel" && origin && best && (
            <span className={`map-legend is-${priceMode}`}><b>{priceMode === "pump" ? "Precio en surtidor" : "Precio con desplazamiento"}</b> · por litro{displayedRoute.estimated ? " · ruta aproximada" : ""}</span>
          )}
          {mapError && <div className="map-error" role="status">{mapError}</div>}
          {mode === "fuel" && origin && pendingAreaCenter && (
            <button className="map-area-button" type="button" onClick={() => { applyOrigin({ ...pendingAreaCenter, label: "Centro del mapa", query: "" }); setNotice(""); }}>
              <Icon name="search" /> Buscar en esta zona
            </button>
          )}
        </section>

        <aside
          className={`panel${isDesktop ? "" : ` is-sheet is-${sheet.snap}${sheet.dragging ? " is-dragging" : ""}`}`}
          style={isDesktop ? undefined : sheet.style}
          aria-label={mode === "fuel" ? "Resultados" : "Cámaras"}
        >
          {!isDesktop && (
            <div className="sheet-grip" role="button" tabIndex={0} aria-label={sheet.snap === "full" ? "Reducir el panel" : "Ampliar el panel"} {...sheet.gripProps}>
              <span className="grab" aria-hidden="true" />
              <span className="sheet-summary">{sheetSummary}</span>
            </div>
          )}
          <div className="panel-scroll" ref={panelScrollRef}>
            {mode === "cameras" ? (
              <CameraPanel
                cameras={cameras}
                selected={selectedCamera}
                onSelect={(id) => setSelectedCameraId(id)}
                onRetry={() => setCameraReloadToken((value) => value + 1)}
                loading={cameraLoading}
                error={cameraError}
                reference={origin}
                referenceLabel={query || "tu punto de partida"}
              />
            ) : (
              <>
                {fuelHead}
                {notice && <div className="notice" role="alert"><span>{notice}</span><button type="button" className="icon-button" aria-label="Cerrar aviso" onClick={() => setNotice("")}><Icon name="close" /></button></div>}
                <div className="panel-body">{fuelBody()}</div>
              </>
            )}
            <footer className="credits">
              {mode === "fuel" ? "Precios: Ministerio para la Transición Ecológica." : "Cámaras: Dirección General de Tráfico."} Mapa: OpenFreeMap y OpenStreetMap. Diseño inspirado en OSIRIS (MIT). Javi L.
            </footer>
          </div>
        </aside>
        <p className="visually-hidden" aria-live="polite">{sheetSummary}</p>
      </main>
    </div>
  );
}
